// wormholescan-status.ts
export type WormholeStatus =
  | "unknown"
  | "in_progress"     // source observed, VAA not yet available
  | "vaa_emitted"     // VAA present, not yet redeemed
  | "completed";      // target chain tx observed

type WormholeScanOperation = {
  id: string;
  emitterChain: number;
  sequence: string;
  vaa?: { raw?: string };
  sourceChain?: { name: string; transaction: { txHash: string }; status: string };
  targetChain?: { name: string; transaction: { txHash: string }; status: string };
  content?: { standarizedProperties?: unknown };
};

type OpsResponse = {
  operations: WormholeScanOperation[];
  pagination?: { page: number; pageSize: number; total: number };
};

const BASE = "https://api.wormholescan.io/api/v1";
const TESTNET_BASE = "https://api.testnet.wormholescan.io/api/v1";

/**
 * Look up a Wormhole operation by a known tx hash (source or destination).
 * @param txHash   EVM/SVM/etc transaction hash (0x… or base58 for Solana)
 * @param opts     { testnet?: boolean }
 */
export async function getWormholeTxStatus(
  txHash: string,
  opts: { testnet?: boolean } = {}
): Promise<{
  status: WormholeStatus;
  sourceTx?: string;
  sourceStatus?: string;
  targetTx?: string;
  targetStatus?: string;
  operation?: WormholeScanOperation;
}> {
  const base = opts.testnet ? TESTNET_BASE : BASE;

  // Try several query shapes the explorer supports.
  const candidates = [
    `${base}/operations?txHash=${encodeURIComponent(txHash)}&pageSize=1`,
    `${base}/operations?sourceTxHash=${encodeURIComponent(txHash)}&pageSize=1`,
    `${base}/operations?targetTxHash=${encodeURIComponent(txHash)}&pageSize=1`,
  ];

  let op: WormholeScanOperation | undefined;

  for (const url of candidates) {
    const r = await fetch(url);
    if (!r.ok) continue;
    const data = (await r.json()) as OpsResponse;
    if (data.operations?.length) {
      // If multiple came back, prefer an exact match on either side.
      op =
        data.operations.find(
          (o) =>
            o.sourceChain?.transaction?.txHash?.toLowerCase() === txHash.toLowerCase() ||
            o.targetChain?.transaction?.txHash?.toLowerCase() === txHash.toLowerCase()
        ) ?? data.operations[0];
      break;
    }
  }

  if (!op) {
    return { status: "unknown" };
  }

  // Derive a compact status (same logic Wormhole docs show).
  // Completed  → targetChain present
  // Emitted    → VAA present but no targetChain yet
  // InProgress → sourceChain present, no VAA yet
  let status: WormholeStatus = "unknown";
  if (op.targetChain) status = "completed";
  else if (op.vaa) status = "vaa_emitted";
  else if (op.sourceChain) status = "in_progress";

  return {
    status,
    sourceTx: op.sourceChain?.transaction?.txHash,
    sourceStatus: op.sourceChain?.status,
    targetTx: op.targetChain?.transaction?.txHash,
    targetStatus: op.targetChain?.status,
    operation: op,
  };
}
